import React, {useState, useEffect} from 'react';
import {toast} from 'react-toastify';
import {useTranslation} from "react-i18next";
import useAxios from "../../../utils/useAxios";
import SelectInput from "../../../components/SelectInput";
import Skeleton from "react-loading-skeleton";
import BannerUpload from "../../../components/BannerUpload";
import FileUpload from "../../../components/FileUpload";
import LocationForm from "../../locations/LocationForm";
import ErrorMessage from "../../../components/ErrorMessage";
import {COUNTRY_OPTIONS, getGradeName, grades} from "../../../constants";
import {getOptionName} from "../../../utils/getOptionName";
import BannerDisplay from "./BannerDisplay";
import TournamentInformation from "./TournamentInformation";
import { useTheme } from "../../../contexts/ThemeContext";
import { patchTournamentListCache } from "../../../utils/patchTournamentListCache";
import { parseDate } from "../../../utils/parseDate";

let clubsCache = null;
let locationsCache = null;

const TournamentInformationForm = ({ tournament, setTournament, loading, setLoading, is_authorized }) => {
    const {t} = useTranslation();
    const { compactTable } = useTheme();
    const [clubs, setClubs] = useState(clubsCache || []);
    let api = useAxios()
    const [errors, setErrors] = useState({});
    const [locations, setLocations] = useState(locationsCache || []);
    const [formData, setFormData] = useState(tournament || {});

    const genders = (formData.grade === 2 || formData.grade === 3) ? [
        {id: 1, name: "Men's"},
        {id: 2, name: "Women's"},
        {id: 3, name: "Mixed"}
    ] : [
        {id: 1, name: "Men's"},
        {id: 2, name: "Women's"},
        {id: 3, name: "Admixed"}
    ];

    useEffect(() => {
        setFormData(tournament || {});
    }, [tournament]);

    useEffect(() => {
        if (!is_authorized || clubsCache) return;

        api.get("/api/v1.0/clubs/?limit=100")
            .then(res => {
                const data = res.data.results || [];
                clubsCache = data;
                setClubs(data);
            })
            .catch(console.error)
    }, [is_authorized]);

    useEffect(() => {
        if (!is_authorized || locationsCache) return;

        let cancelled = false;

        api.get('/api/v1.0/locations/?limit=150')
            .then(res => {
                if (!cancelled) {
                    const data = res.data.results;
                    locationsCache = data;
                    setLocations(data);
                }
            })
            .catch(() => toast.error(t("Failed to load locations")));

        return () => { cancelled = true; };
    }, [is_authorized]);

    const handleBlur = async (e) => {
        const { name, value } = e.target;
        if (formData[name] === value) return;

        if (name === "start_date" || name === "end_date") {
            const start = parseDate(name === "start_date" ? value : formData.start_date);
            const end = parseDate(name === "end_date" ? value : formData.end_date);
            if (start && end && end < start) {
                setErrors(prev => ({ ...prev, end_date: "End date cannot be earlier than start date" }));
                return;
            }
        }

        setFormData(prev => ({ ...prev, [name]: value }));
        await handleSubmit({ [name]: value });
    };

    const handleSubmit = async (fieldsToUpdate = {}) => {
        setErrors({});
        const formDataToSend = new FormData();

        Object.entries(fieldsToUpdate).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                formDataToSend.append(key, value);
            }
        });

        if (Object.keys(fieldsToUpdate).length === 0) return;

        try {
            const response = await api.patch(`/api/v1.0/tournaments/${tournament.id}/`, formDataToSend);

            if (response.status === 200) {
                setTournament(prev => ({ ...prev, ...fieldsToUpdate }));
                if (fieldsToUpdate.name) document.title = fieldsToUpdate.name;
                // So главная's own scroll-restore snapshot doesn't keep
                // showing this tournament's pre-edit name/dates/format for
                // up to 10 minutes - see patchTournamentListCache.js.
                patchTournamentListCache(tournament.id, fieldsToUpdate);
                }
        } catch (error) {
            if (error.response?.status === 400) {
                setErrors(error.response.data || {});
            } else {
                toast.error('Something went wrong!');
            }
        }

    };

    if (!is_authorized) {
        return (
            <TournamentInformation tournament={formData} />
        );
    }

    return (
        <>
            {/*<ToggleTournamentBlock tournamentId={tournament?.id} blockType={1}/>*/}
            <div
                id="information-block"
                className="tournament-form"
                style={{ marginTop: "15px" }}
            >
                <div>
                    <div className="content_page">
                        {is_authorized && clubs.length > 1 &&
                            <div>
                                {loading ? (
                                    <>
                                        <label>{t("Club")}</label>
                                        <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                    </>

                                ) : (
                                    <SelectInput
                                    default_text={t("All clubs")}
                                    data={clubs}
                                    default_value={formData.club}
                                    label_text={t("Club")}
                                    input_name="club"
                                    setInputValue={(value) => {
                                        setFormData(prev => ({ ...prev, club: value }));
                                        handleSubmit({ club: value });
                                    }}
                                    onChange={handleBlur}
                                />
                                )}
                            </div>
                        }
                        <div>
                            <label>{t("Name")}</label>

                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : (
                                <input
                                    type="text"
                                    name="name"
                                    defaultValue={formData.name || ''}
                                    onBlur={handleBlur}
                                    disabled={!is_authorized}
                                    className={!is_authorized ? "unauthorized" : ""}
                                />
                            )
                            }
                            <div id="match-text" className={`up ${errors.name ? "active" : "hide"}`}>
                                <ErrorMessage error={t(errors.name)}/>
                            </div>
                        </div>
                        <div>
                            <label>{t("Start Date")}</label>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : (
                                <input
                                    type="datetime-local"
                                    name="start_date"
                                    defaultValue={formData.start_date || ''}
                                    onBlur={is_authorized && handleBlur}
                                    onFocus={(e) => is_authorized && e.target.showPicker()}
                                    disabled={!is_authorized}
                                    className={!is_authorized ? "unauthorized" : ""}
                                />
                            )
                            }
                            <ErrorMessage error={t(errors.start_date)}/>
                        </div>
                        <div>
                            <label>{t("End Date")}</label>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : (
                                <input
                                    type="datetime-local"
                                    name="end_date"
                                    defaultValue={formData.end_date || ''}
                                    onBlur={is_authorized && handleBlur}
                                    onFocus={(e) => is_authorized && e.target.showPicker()}
                                    disabled={!is_authorized}
                                    className={!is_authorized ? "unauthorized" : ""}
                                />
                            )
                            }
                            <ErrorMessage error={t(errors.end_date)}/>
                        </div>
                        {
                            is_authorized ?
                                (
                                    <div
                                        style={{
                                            display: 'flex',
                                            width: '100%',
                                            gap: '10px',
                                            padding: 0,
                                            margin: 0,
                                            marginTop: "-5px",
                                            marginBottom: "-20px"
                                        }}
                                    >
                                        <div
                                            style={{
                                                flex: 1,
                                                padding: 0,
                                                margin: 0,
                                            }}
                                        >
                                            {loading ? (
                                                <>
                                                    <label>{t("Format")}</label>
                                                    <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'} />
                                                </>
                                            ) :
                                                <SelectInput
                                                    default_text={t("Choose grade")}
                                                    data={grades}
                                                    default_value={formData.grade}
                                                    label_text={t("Format")}
                                                    input_name="grade"
                                                    setInputValue={(value) => {
                                                        setFormData(prev => ({ ...prev, grade: value }));
                                                        handleSubmit({ grade: value });
                                                    }}
                                                    onChange={handleBlur}
                                                />
                                                }
                                            <ErrorMessage error={t(errors.grade)} />
                                        </div>
                                        <div
                                            style={{
                                                flex: 1,
                                                padding: 0,
                                                margin: 0,
                                            }}
                                        >{loading ? (
                                            <>
                                                <label>&nbsp;</label>
                                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                            </>
                                        ) : (
                                            <SelectInput
                                                default_text={t("Choose gender")}
                                                data={genders}
                                                default_value={formData.gender}
                                                input_name="gender"
                                                setInputValue={(value) => {
                                                    setFormData(prev => ({ ...prev, gender: value }));
                                                    handleSubmit({ gender: value });
                                                }}
                                            />
                                        )}<ErrorMessage error={t(errors.gender)}/>
                                        </div>
                                    </div>
                                )
                                :
                                loading ? (
                                                <>
                                                    <label>&nbsp;</label>
                                                    <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                                </>
                                            ) :
                                            <>
                                                <label>{t("Format")}</label>
                                                <input
                                                    type="text"
                                                    value={
                                                        formData.grade
                                                            ? `${getGradeName(formData.grade)}, ${
                                                                formData.gender ? t(`gender.${formData.gender}`) : ''
                                                            }`
                                                            : ''
                                                    }
                                                    disabled
                                                    className="unauthorized"
                                                />
                                            </>

                        }
                        {/*{!loading && <div className="up"></div>}*/}
                        <div>
                            {loading ? (
                                <>
                                    <label>{t("Location")}</label>
                                    <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                                </>
                            ): is_authorized ? (
                                <SelectInput
                                    default_text={t("Choose location")}
                                    data={locations}
                                    setData={setLocations}
                                    default_value={formData.location}
                                    label_text={t("Location")}
                                    input_name="location"
                                    setInputValue={(value) => {
                                        setFormData(prev => ({ ...prev, location: value }));
                                        handleSubmit({ location: value });
                                    }}
                                    create_form={LocationForm}
                                    search={true}
                                />
                            ) : (<>
                                <label>{t("Location")}</label>
                                <input
                                    type="text"
                                    value={`${formData.location_name} ${formData.city}${formData.city && formData.country ? ", " : ""}${formData.country ? t(getOptionName(COUNTRY_OPTIONS, formData.country)) : ""}`}
                                    disabled
                                    className="unauthorized"
                                />
                            </>)}
                            <ErrorMessage error={t(errors.location)}/>
                        </div>

                        <div>
                            <label>{t("Level")}</label>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : (
                                <input
                                    type="text"
                                    name="format"
                                    defaultValue={formData.format || ''}
                                    onBlur={handleBlur}
                                    disabled={!is_authorized}
                                    className={!is_authorized ? "unauthorized" : ""}
                                />
                            )
                            }
                            <ErrorMessage error={t(errors.format)}/>
                        </div>
                        <div>
                            <label>{t("Age")}</label>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : (
                                <input
                                    type="text"
                                    name="age"
                                    defaultValue={formData.age || ''}
                                    onBlur={handleBlur}
                                    disabled={!is_authorized}
                                    className={!is_authorized ? "unauthorized" : ""}
                                />
                            )
                            }
                            <ErrorMessage error={t(errors.age)}/>
                        </div>
                        <div>
                            <label>{t("Organizers and sponsors")}</label>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : (
                                <input
                                    type="text"
                                    name="participation"
                                    defaultValue={formData.participation || ''}
                                    onBlur={handleBlur}
                                    disabled={!is_authorized}
                                    className={!is_authorized ? "unauthorized" : ""}
                                />
                            )
                            }
                            <ErrorMessage error={t(errors.participation)}/>
                        </div>
                        <div>
                            <label>{t("Drawing")}</label>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : (
                                <input
                                    type="text"
                                    name="sort"
                                    defaultValue={formData.sort || ''}
                                    onBlur={handleBlur}
                                    disabled={!is_authorized}
                                    className={!is_authorized ? "unauthorized" : ""}
                                />
                            )
                            }
                            <ErrorMessage error={t(errors.sort)}/>
                        </div>
                        <div>
                            <label>{t("Head referee")}</label>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : (
                                <input
                                    type="text"
                                    name="tasks"
                                    defaultValue={formData.tasks || ''}
                                    onBlur={handleBlur}
                                    disabled={!is_authorized}
                                    className={!is_authorized ? "unauthorized" : ""}
                                />
                            )
                            }
                            <ErrorMessage error={t(errors.tasks)}/>
                        </div>
                        <div>
                            <label>{t("Money")}</label>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : (
                                <input
                                    type="text"
                                    name="money"
                                    defaultValue={formData.money || ''}
                                    onBlur={handleBlur}
                                    disabled={!is_authorized}
                                    className={!is_authorized ? "unauthorized" : ""}
                                />
                            )
                            }
                            <ErrorMessage error={t(errors.money)}/>
                        </div>
                        <div>
                            <label>{t("Prize Fund")}</label>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : (
                                <input
                                    type="text"
                                    name="prize_fund"
                                    defaultValue={formData.prize_fund || ''}
                                    onBlur={handleBlur}
                                    disabled={!is_authorized}
                                    className={!is_authorized ? "unauthorized" : ""}
                                />
                            )
                            }
                            <ErrorMessage error={t(errors.prize_fund)}/>
                        </div>
                        <div>
                            <label>{t("Official Ball and court surface")}</label>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : (
                                <input
                                    type="text"
                                    name="official_ball"
                                    defaultValue={formData.official_ball || ''}
                                    onBlur={handleBlur}
                                    disabled={!is_authorized}
                                    className={!is_authorized ? "unauthorized" : ""}
                                />
                            )
                            }
                            <ErrorMessage error={t(errors.official_ball)}/>
                        </div>
                        <div>
                            <label>{t("Award")}</label>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : (
                                <input
                                    type="text"
                                    name="award"
                                    defaultValue={formData.award || ''}
                                    onBlur={handleBlur}
                                    disabled={!is_authorized}
                                    className={!is_authorized ? "unauthorized" : ""}
                                />
                            )
                            }
                            <ErrorMessage error={t(errors.award)}/>
                        </div>
                        <div>
                            <label>{t("Contact data")}</label>
                            {loading ? (
                                <Skeleton height={42} width={'100%'} count={1} baseColor={'#f8f7fa'}/>
                            ) : (
                                <input
                                    type="text"
                                    name="phone"
                                    defaultValue={formData.phone || ''}
                                    onBlur={handleBlur}
                                    disabled={!is_authorized}
                                    className={!is_authorized ? "unauthorized no-border" : ""}
                                />
                            )
                            }
                            <ErrorMessage error={t(errors.award)}/>
                        </div>
                    </div>
                </div>
                <div className="sidebar-wrapper">
                    <div className={!is_authorized ? "no-mobile" : ""}>
                        {
                            is_authorized ? (
                                <BannerUpload
                                    id={tournament?.id}
                                    image={formData.image}
                                    width={formData.width}
                                    height={formData.height}
                                    setImage={(image) => setFormData((prev) => ({ ...prev, image }))}
                                    loading={loading}
                                />
                            ) : (() => {
                                // Same "no substitute banner in Compact table mode" rule as
                                // TournamentInformation.js's public view - location_image is
                                // the venue's own stock photo, not this tournament's, and
                                // showing it as if it were the tournament's banner reads as
                                // misleading rather than helpful once the surrounding UI is
                                // already leaning minimal/no-filler.
                                const hasOwnImage = !!formData.image;
                                const showLocationFallback = !compactTable && !hasOwnImage && !!formData.location_image;
                                const bannerSrc = formData.image?.url || formData.image || (showLocationFallback ? formData.location_image : null);
                                if (!bannerSrc) return null;
                                return (
                                    <BannerDisplay
                                        image={bannerSrc}
                                        width={hasOwnImage ? formData.width : (showLocationFallback ? formData.location_width : formData.width)}
                                        height={hasOwnImage ? formData.height : (showLocationFallback ? formData.location_height : formData.height)}
                                    />
                                );
                            })()}
                    </div>

                    <div>
                        <FileUpload
                            id={tournament?.id}
                            file={formData.file}
                            setFile={(file) => setFormData((prev) => ({ ...prev, file }))}
                            loading={loading}
                            is_authorized={is_authorized}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default TournamentInformationForm;
